import type { OrchestratedQuote } from '@b-stock/fe-core/bstock-next/queries'
import { TmsErrorCode, type QuoteDto } from '@b-stock/shipment-api-client'

/**
 * The quote-set query (`quoteSetQuery`) returns a mix of base `QuoteDto`s and
 * `OrchestratedQuote`s (the latter only differ in `origin`/`destination`
 * carrying full location data). Every field the parcel rate UI reads is shared
 * between the two, so the rate components accept either shape.
 */
export type ParcelQuote = QuoteDto | OrchestratedQuote

/**
 * The `tmsMetaData.errorCode` values that the backend attaches to a zero-cost
 * placeholder quote when a parcel listing has no usable rate, each with its own
 * buyer-facing message.
 */
export const PARCEL_NO_RATES_ERROR_CODES = [
  TmsErrorCode.NoRatesFound,
  TmsErrorCode.ParcelNotSupported,
  TmsErrorCode.ParcelVendorUnavailable,
] as const

export type ParcelNoRatesErrorCode =
  (typeof PARCEL_NO_RATES_ERROR_CODES)[number]

/**
 * Map each backend error code to its canned, localized buyer copy key. Unknown
 * codes are resolved to NO_RATES_FOUND before they reach this map (see
 * detectNoRates.ts), so it is exhaustive over the known codes.
 *
 * Only this canned copy is shown, never the raw carrier `tmsMetaData.message`.
 */
export const PARCEL_NO_RATES_MESSAGE_KEYS: Record<
  ParcelNoRatesErrorCode,
  string
> = {
  NO_RATES_FOUND: 'ParcelRateList.noRates.noRatesFound',
  PARCEL_NOT_SUPPORTED: 'ParcelRateList.noRates.notSupported',
  PARCEL_VENDOR_UNAVAILABLE: 'ParcelRateList.noRates.vendorUnavailable',
}
